import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/daolm9kvvta8mhpmer20";

const deviceOptions = [
  "MacBook Air",
  "MacBook Pro",
  "iMac",
  "Mac mini",
  "Mac Studio",
  "기타 맥 제품",
];

const symptomOptions = [
  "전원이 켜지지 않음",
  "화면 불량 / 액정 손상",
  "배터리 / 충전 문제",
  "키보드 / 트랙패드 불량",
  "침수",
  "발열 / 소음",
  "데이터 복구",
  "기타 증상",
];

export default function InquiryForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("문의가 정상적으로 접수되었습니다. 확인 후 빠르게 연락드리겠습니다.");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSuccess = response.ok && parsed?.code === "OK";

      if (isSuccess) {
        setStatus("success");
        setFeedback("문의가 정상적으로 접수되었습니다. 확인 후 빠르게 연락드리겠습니다.");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          serverMsg && !serverMsg.toLowerCase().includes("spam")
            ? serverMsg
            : "문의 접수에 실패했습니다. 잠시 후 다시 시도하시거나 전화로 문의해 주세요.",
        );
      }
    } catch {
      setStatus("error");
      setFeedback("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

  return (
    <section id="inquiry" className="w-full bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <SectionHeading
          eyebrow="Online Inquiry"
          title="증상만 알려주시면"
          highlight="바로 상담해 드려요"
          desc="접수 후 담당 엔지니어가 확인하여 순차적으로 연락드립니다. 급한 수리는 전화 상담을 이용해 주세요."
        />

        <div className="mx-auto mt-14 w-full max-w-2xl">
          <div className="reveal rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-checkbox-circle-fill text-4xl"></i>
                </span>
                <h3 className="mt-6 font-heading text-2xl font-black text-foreground-950">
                  문의가 접수되었습니다
                </h3>
                <p className="mt-3 max-w-sm font-body text-sm leading-relaxed text-foreground-600">
                  {feedback}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStatus("idle");
                    setFeedback("");
                  }}
                  className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line"></i>
                  새 문의 작성
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form encType="application/x-www-form-urlencoded" noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      이름 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="홍길동"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      연락처 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="010-0000-0000"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      이메일
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="device" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      맥 모델 <span className="text-primary-500">*</span>
                    </label>
                    <select id="device" name="device" required defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        모델을 선택하세요
                      </option>
                      {deviceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="symptom" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      증상 유형 <span className="text-primary-500">*</span>
                    </label>
                    <select id="symptom" name="symptom" required defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        증상을 선택하세요
                      </option>
                      {symptomOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="mb-2 block font-label text-sm font-bold text-foreground-800">
                      상세 증상
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="언제부터, 어떤 상황에서 문제가 발생했는지 자세히 적어주시면 더 정확한 안내가 가능합니다. (최대 500자)"
                      className={`${inputClass} resize-y`}
                    ></textarea>
                  </div>
                </div>

                {/* anti-spam honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="mfx-form-note"
                />

                <label className="mt-5 flex items-start gap-2.5 font-body text-xs leading-relaxed text-foreground-600">
                  <input
                    type="checkbox"
                    name="privacy_agree"
                    value="동의"
                    required
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-background-300 text-primary-500 focus:ring-primary-300"
                  />
                  개인정보 수집 및 이용에 동의합니다. 수집된 정보는 수리 상담 목적으로만 사용됩니다.
                </label>

                {status === "error" && (
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 font-body text-sm font-medium text-accent-900">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {feedback}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 font-label text-base font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      접수 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-lg"></i>
                      무료 진단 문의 접수하기
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}